import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { AI_PROVIDERS, AI_PROVIDER_LABEL, type AIProvider } from '../../types';
import { fetchAiKeyStatus } from '../../api/reportApi';

// "Which AI?" as buttons: one per provider the user already saved a key for,
// plus an always-present link to edit the keys of every provider. When no
// provider has a key it says so instead of silently offering a keyless one.
// The choice is the same `aiSettings.provider` the AI reports and the AI
// integration screen read, so picking here switches them all.
export function AiProviderPicker({ className = '' }: { className?: string }) {
  const { aiSettings, setAiSettings } = useStore();
  // A key saved on the account is never sent back to the browser, so ask the
  // backend which providers have one; the server uses it when a request has none.
  const [saved, setSaved] = useState<AIProvider[]>([]);
  useEffect(() => {
    fetchAiKeyStatus()
      .then((list) => setSaved(list.filter((s) => s.saved).map((s) => s.provider)))
      .catch(() => {});
  }, []);
  const withKey = AI_PROVIDERS.filter((p) => (aiSettings.apiKeys[p] ?? '').trim() !== '' || saved.includes(p));
  const current = aiSettings.provider;
  const currentHasKey = withKey.includes(current);

  // The saved provider (default: DeepSeek) may be the one with no key while
  // another has one - start on a provider that can actually run.
  useEffect(() => {
    if (withKey.length > 0 && !currentHasKey) setAiSettings({ ...aiSettings, provider: withKey[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withKey.join(','), currentHasKey]);

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="AI provider">
        {withKey.map((p) => {
          const selected = p === current;
          return (
            <button
              key={p}
              type="button"
              aria-pressed={selected}
              onClick={() => setAiSettings({ ...aiSettings, provider: p })}
              className={`rounded-full px-3 py-1 text-[13px] font-medium transition-colors ${
                selected
                  ? 'bg-blue-600 text-white'
                  : 'border border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10'
              }`}
            >
              {AI_PROVIDER_LABEL[p]}
            </button>
          );
        })}
        <Link
          to="/settings/ai"
          className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          <KeyRound size={13} aria-hidden /> {withKey.length === 0 ? 'Add AI keys' : 'Edit AI keys'}
        </Link>
      </div>
      {withKey.length === 0 && (
        <p role="status" className="m-0 text-xs text-amber-600 dark:text-amber-400">
          No AI key saved yet - add one for the AI you use (ChatGPT, DeepSeek, Claude...) to run this.
        </p>
      )}
    </div>
  );
}
